import { Close, LinkedIn } from "@mui/icons-material";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface LinkedInPostImportDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: Function;
  companyId: string;
  listId: string;
  onSuccess: () => void;
}

const LinkedInPostImportDialog: React.FC<LinkedInPostImportDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  companyId,
  listId,
  onSuccess,
}) => {
  const [postUrl, setPostUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateLinkedInUrl = (url: string): boolean => {
    return (
      url.includes("linkedin.com/posts/") ||
      url.includes("linkedin.com/feed/update/")
    );
  };

  const handleImport = async () => {
    setError(null);

    // Validate URL
    if (!postUrl.trim()) {
      setError("Please enter a LinkedIn post URL");
      return;
    }

    if (!validateLinkedInUrl(postUrl)) {
      setError(
        "Please enter a valid LinkedIn post URL (e.g., linkedin.com/posts/...)"
      );
      return;
    }

    setLoading(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/lists/${listId}/import/linkedin-post`,
        {
          companyId,
          postUrl: postUrl.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        try {
          eventListener({
            type: "SET_SUCCESS",
            message: `Successfully imported ${
              response.data.imported
            } items from LinkedIn post${
              response.data.postData
                ? ` - Author: ${response.data.postData.authorName}, Likes: ${response.data.postData.likesCount}, Comments: ${response.data.postData.commentsCount}`
                : ""
            }`,
          });
        } catch (eventError) {
          console.error("EventListener error:", eventError);
        }
        handleClose();
        onSuccess();
      }
    } catch (err: any) {
      const errorMessage = parseError(err);
      setError(errorMessage);
      try {
        eventListener({
          type: "SET_ERROR",
          error: `Failed to import LinkedIn post: ${errorMessage}`,
        });
      } catch (eventError) {
        console.error("EventListener error:", eventError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPostUrl("");
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <LinkedIn color="primary" />
            <Typography variant="h6">Import LinkedIn Post</Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            disabled={loading}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Import a LinkedIn post and its comments into your smart list. This
            will consume <strong>10 credits</strong>.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="LinkedIn Post URL"
            value={postUrl}
            onChange={(e) => setPostUrl(e.target.value)}
            placeholder="https://www.linkedin.com/posts/..."
            helperText="Enter the full URL of a LinkedIn post (query parameters will be automatically removed)"
            disabled={loading}
            autoFocus
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            The import will extract:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Post commentors</li>
              <li>Post engagement users</li>
            </ul>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <LoadingButton onClick={handleClose} disabled={loading} variant="text">
          Cancel
        </LoadingButton>
        <LoadingButton
          onClick={handleImport}
          loading={loading}
          variant="contained"
          color="primary"
        >
          Import Post (10 Credits)
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default LinkedInPostImportDialog;
